import type { MouseEvent, ReactNode } from 'react';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { useStyles } from './styles';

export interface DataGridColumn<T> {
  key: string;
  header?: ReactNode;
  /** a CSS grid track: '137px', 'minmax(320px, 1fr)' */
  width: string;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => ReactNode;
  /** placeholder while loading; a text bar when left out, nothing when null */
  skeleton?: ReactNode;
  /** drop the cell padding, e.g. for a button that fills the cell */
  flush?: boolean;
}

interface DataGridProps<T> {
  columns: DataGridColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** renders skeleton rows built from the same columns */
  loading?: boolean;
  skeletonRows?: number;
  /** below this the grid scrolls sideways instead of squeezing */
  minWidth?: number;
  /**
   * Mouse shortcut for the row's main action. Not focusable on purpose: every row
   * must also offer that action as a real control in one of its cells.
   */
  onRowClick?: (row: T) => void;
  /** content under the row, e.g. an expanded detail; null hides it */
  renderExpanded?: (row: T) => ReactNode;
  /** lined: rule under each row · cards: rounded rows with gaps */
  variant?: 'lined' | 'cards';
  striped?: boolean;
  rowMinHeight?: number;
  empty?: ReactNode;
  footer?: ReactNode;
}

/** Clicks on a control inside the row belong to that control, not the row. */
const fromControl = (e: MouseEvent) =>
  Boolean(
    (e.target as HTMLElement).closest(
      'button, a, input, select, textarea, label, [role="button"]'
    )
  );

/**
 * PDV's table: a CSS grid whose header, rows and loading skeleton all come from one
 * column list, so they cannot drift apart.
 */
export const DataGrid = <T,>({
  columns,
  rows,
  getRowKey,
  loading,
  skeletonRows = 8,
  minWidth = 900,
  onRowClick,
  renderExpanded,
  variant = 'lined',
  striped,
  rowMinHeight = 68,
  empty,
  footer
}: DataGridProps<T>) => {
  const classes = useStyles();
  const template = columns.map(c => c.width).join(' ');
  const cards = variant === 'cards';

  const cellClass = (c: DataGridColumn<T>) =>
    clsx(
      classes.cell,
      cards && classes.cardCell,
      classes[c.align ?? 'left'],
      c.flush && classes.flush
    );

  const row = (key: string, cells: ReactNode, opts: { item?: T } = {}) => {
    const { item } = opts;
    const clickable = Boolean(onRowClick && item);
    const expanded = item && renderExpanded ? renderExpanded(item) : null;
    return (
      <div
        key={key}
        className={clsx(
          cards ? classes.cardRow : classes.linedRow,
          !cards && striped && classes.striped
        )}>
        {/* The row click is a mouse shortcut; the same action is a focusable
            control in one of the cells, so keyboard users are not left out. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className={clsx(
            classes.grid,
            !cards && classes.linedGrid,
            clickable && classes.clickable
          )}
          style={{ gridTemplateColumns: template, minHeight: rowMinHeight }}
          onClick={
            clickable
              ? e => {
                  if (!fromControl(e)) onRowClick?.(item as T);
                }
              : undefined
          }>
          {cells}
        </div>
        {expanded}
      </div>
    );
  };

  return (
    <div className={classes.scroll}>
      <div style={{ minWidth }}>
        <div className={classes.head} style={{ gridTemplateColumns: template }}>
          {columns.map(c => (
            <span
              key={c.key}
              className={clsx(classes.headCell, classes[c.align ?? 'left'])}>
              {c.header}
            </span>
          ))}
        </div>

        {loading
          ? Array.from({ length: skeletonRows }, (_, i) =>
              row(
                `skeleton-${i}`,
                columns.map(c => (
                  <div key={c.key} className={cellClass(c)}>
                    {c.skeleton !== undefined ? (
                      c.skeleton
                    ) : (
                      <Skeleton variant="text" width="60%" />
                    )}
                  </div>
                ))
              )
            )
          : rows.map((item, i) =>
              row(
                getRowKey(item),
                columns.map(c => (
                  <div key={c.key} className={cellClass(c)}>
                    {c.render(item, i)}
                  </div>
                )),
                { item }
              )
            )}

        {!loading && !rows.length && empty && (
          <div className={classes.empty}>{empty}</div>
        )}
      </div>
      {footer}
    </div>
  );
};
